import type { ReactNode } from "react";
import { Lock, Pencil, Unlock } from "lucide-react";
import { useAppStore } from "@/store/store";
import { useAuthStore } from "@/store/auth-store";
import { Button } from "@/components/ui/button";

/** Bottom-centered floating pill — Figma-inspired shell shared by editor and dashboard. */
export function MapToolbar({ children }: { children: ReactNode }) {
  return (
    <div
      className="absolute left-1/2 z-20 bg-white rounded-[22px] max-w-[96vw] border border-gray-200"
      style={{
        bottom: "calc(24px + var(--safe-bottom))",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
        transform: "translateX(-50%)",
      }}
    >
      <div className="flex items-center gap-1 px-2 py-2">{children}</div>
    </div>
  );
}

export function MapToolbarDivider() {
  return <div className="w-px h-6 bg-gray-200 mx-1" />;
}

interface MapToolbarButtonProps {
  onClick: () => void;
  label?: string;
  active?: boolean;
  children: ReactNode;
}

/** Icon-lg square button — active uses brand green (#22c55e), inactive hovers into gray. */
export function MapToolbarButton({ onClick, label, active, children }: MapToolbarButtonProps) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      size="icon-lg"
      title={label}
      aria-label={label}
      className={`rounded-xl transition-colors ${
        active
          ? "bg-[#22c55e] text-white hover:bg-[#22c55e]/90 hover:text-white"
          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
      }`}
    >
      {children}
    </Button>
  );
}

export function MapToolbarModeToggle() {
  const mapMode = useAppStore((s) => s.mapMode);
  const setMapMode = useAppStore((s) => s.setMapMode);
  const isEdit = mapMode === "edit";

  return (
    <MapToolbarButton
      onClick={() => setMapMode(isEdit ? "view" : "edit")}
      label={isEdit ? "Salir del modo edición" : "Entrar al modo edición"}
      active={isEdit}
    >
      <Pencil className="size-4" />
    </MapToolbarButton>
  );
}

export function MapToolbarLockToggle() {
  const locationId = useAuthStore((s) => s.locationId);
  const mapLocked = useAppStore((s) => (locationId ? !!s.mapLockedByLocation[locationId] : false));
  const setMapLocked = useAppStore((s) => s.setMapLocked);

  if (!locationId) return null;

  return (
    <MapToolbarButton
      onClick={() => setMapLocked(locationId, !mapLocked)}
      label={mapLocked ? "Desbloquear mapa" : "Bloquear mapa"}
      active={mapLocked}
    >
      {mapLocked ? <Lock className="size-4" /> : <Unlock className="size-4" />}
    </MapToolbarButton>
  );
}
